import { toBN } from '@derivation-tech/context';
import { ContractReceipt } from '@ethersproject/contracts';
import { JsonRpcProvider, JsonRpcSigner } from '@ethersproject/providers';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { BigNumber } from 'ethers';
import _ from 'lodash';

import { FETCHING_STATUS, OPERATION_TX_TYPE } from '@/constants';
import { TRADE_LEVERAGE_THRESHOLDS } from '@/constants/global';
import {
  ERROR_MSG_EXCEED_WALLET_BALANCE,
  ERROR_MSG_FAIR_FAR_FROM_MARK,
  ERROR_MSG_LIMIT_PRICE_FAR_FROM_MARK,
  ERROR_MSG_ORDER_OCCUPIED,
  ERROR_MSG_SIZE_TO_TICK_IS_TRIVIAL,
} from '@/constants/simulation';
import { INVALID_PRICE, LEVERAGE_ADJUST_TYPE, MANAGE_SIDE, TRADE_TYPE } from '@/constants/trade';
import { PriceBasisForPnl } from '@/constants/trade/priceBasisForPnl';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedOrder } from '@/entities/WrappedOrder';
import { WrappedPair } from '@/entities/WrappedPair';
import { WrappedPortfolio } from '@/entities/WrappedPortfolio';
import { WrappedPosition } from '@/entities/WrappedPosition';
import {
  AdjustMarginByLeverageState,
  IAdjustMarginSimulation,
  ICrossMarketOrderData,
  ICrossMarketOrderSimulation,
  IMarketFormState,
  IOrderSimulation,
  IOrderSimulationData,
  IPnlParams,
  IScaledOrderSimulationData,
  ISimulation,
  ISimulationData,
  MarginAdjustMethod,
} from '@/types/trade';
import { IParsedTemplate } from '@/types/transaction';
import { getMappedRevertReason, parsedEthersError } from '@/utils/error';
import { parseSendingTxMessageMapping } from '@/utils/notification';
import { formatEther, toWad } from '@/utils/numberUtil';
import { showProperDateString } from '@/utils/timeUtils';
import { addedDeadline, getLimitAlignPriceWad, perToTenThousands } from '@/utils/trade';

import { QUERY_KEYS } from '@/constants/query';
import SentryService from '@/entities/SentryService';
import { queryClient } from '@/global';
import { INotificationArgs } from '@/hooks/useTxNotification';

import { IMetaOrder } from '@/types/account';
import { IGateValueResponse } from '@/types/balance';
import { getSynContract } from '@/utils/contract';
import { getAdjustTradePrice, getAdjustTradeSide } from '@/utils/pairs';
import { calcMarginToDeposit } from '@/utils/position';
import { handleSendAgentOrNormalTx } from '@/utils/tx';
import { Context } from '@derivation-tech/context';
import {
  BatchOrderSizeDistribution,
  DEFAULT_REFERRAL_CODE,
  InstrumentInfo,
  MAX_CANCEL_ORDER_COUNT,
  PlaceMarketOrderParam,
  PlaceParam,
  Quotation,
  Side,
  SimulateTradeResult,
  TickMath,
  ZERO,
  utils,
} from '@synfutures/sdks-perp';
import { FundingChartInterval, KlineInterval } from '@synfutures/sdks-perp-datasource';
import { fetchGateBalanceAction } from '../balance/actions';
import { fetchFuturesInstrumentInquire, fetchFuturesInstrumentInquireByTick } from '../futures/api';
import { useGlobalStore } from '../global/stores';
import { PollingHistoryId } from '../global/type';

import { AxiosError } from 'axios';
import { ITokenBalanceFromApiResponse } from '../balance/types';
import { AppState } from '../store';
import { sendTransaction } from '../transaction/actions';
import { checkTokenAllowance } from '../user/actions';

export const resetFormByChainId = createAction<{ chainId: number }>('trade/resetFormByChainId');
export const setMarketFormState = createAction<IMarketFormState>('trade/setMarketFormState');
export const setCurrentFutures = createAction<{ chainId: number; futures: InstrumentInfo }>('trade/setCurrentFuture');
export const setCurrentPair = createAction<{ chainId: number; pair: WrappedPair }>('trade/setCurrentPair');
export const clearMarketTradeSimulation = createAction<{ chainId: number }>('trade/clearMarketTradeSimulation');

export const resetAdjustMarginForm = createAction<{ chainId: number }>('trade/resetAdjustMarginForm');
export const resetClosePositionForm = createAction<{ chainId: number }>('trade/resetClosePositionForm');
export const setPairChartDuration = createAction<{ chainId: number; chartDuration: KlineInterval }>(
  'trade/setPairChartDuration',
);
export const setFundingChartDuration = createAction<{ chainId: number; chartDuration: FundingChartInterval }>(
  'trade/setFundingChartDuration',
);
export const setPairStepRatio = createAction<{ chainId: number; stepRatio: number }>('trade/setPairStepRatio');
export const setPairLeverageSwitchChecked = createAction<{
  chainId: number;
  checked: boolean;
  userAddr: string;
  pairId: string;
}>('trade/setPairLeverageSwitchChecked');

export const switchToMarketTab = createAction<{ chainId: number }>('trade/switchToMarketTab');

// market and adjust leverage form
export const setTradeFormLeverage = createAction<{
  chainId: number;
  leverage: string | undefined;
}>('trade/setTradeFormLeverage');
export const setTradeFormLeverageAndMargin = createAction<{
  chainId: number;
  leverage: string | undefined;
  marginAmount: string | undefined;
}>('trade/setTradeFormLeverageAndMargin');

export const setTradeFormType = createAction<{
  chainId: number;
  tradeType: TRADE_TYPE;
}>('tradeForm/setTradeFormType');

export const setPriceBasisForPnl = createAction<{
  priceBasisForPnl: PriceBasisForPnl;
}>('tradeForm/setPriceBasisForPnl');

export const setTradeDrawerType = createAction<{
  chainId: number;
  tradeType: TRADE_TYPE | undefined;
}>('tradeForm/setTradeDrawerType');

// market and limit order form
export const setTradeFormSide = createAction<{
  chainId: number;
  tradeSide: Side;
}>('tradeForm/setTradeFormSide');

// market and limit order form
export const setTradeFormAmount = createAction<{
  chainId: number;
  baseAmount?: string;
  quoteAmount?: string;
  marginAmount?: string;
  leverageAdjustType?: LEVERAGE_ADJUST_TYPE;
}>('tradeForm/setTradeFormAmount');
export const setScaleFormAmount = createAction<{
  chainId: number;
  baseAmount?: string;
  quoteAmount?: string;
  orderCount?: number;
  stepRatio?: number;
  sizeDistribution?: BatchOrderSizeDistribution;
}>('tradeForm/setScaleFormAmount');

// close position form
export const setClosePositionFormAmount = createAction<{
  chainId: number;
  amount: string;
}>('tradeForm/setClosePositionFormAmount');

// adjust margin form
export const setAdjustMarginMethod = createAction<MarginAdjustMethod>('tradeForm/setAdjustMarginMethod');
export const setAdjustMarginSide = createAction<{ method: MarginAdjustMethod; side: MANAGE_SIDE }>(
  'tradeForm/setAdjustMarginSide',
);

// adjust margin  form
export const setAdjustMarginByLeverageState = createAction<
  {
    chainId: number;
  } & Partial<AdjustMarginByLeverageState>
>('tradeForm/setAdjustMarginByLeverageState');

export const setAdjustMarginFormAmount = createAction<{
  chainId: number;
  amount: string;
  transferIn?: boolean;
  marginToDeposit?: string;
}>('tradeForm/setAdjustMarginFormAmount');
// adjust margin simulation
export const setAdjustMarginSimulation = createAction<{
  chainId: number;
  transferAmount: BigNumber;
  marginToDepositWad: BigNumber;
  leverageWad: BigNumber;
}>('tradeForm/setAdjustMarginSimulation');

// market and limit order form
export const setTradeFormMargin = createAction<{
  chainId: number;
  marginAmount: string;
}>('tradeForm/setTradeFormMargin');

// limit order form only
export const setScaleLimitPrice = createAction<{
  chainId: number;
  lowerPrice?: string;
  alignedLowerPrice?: string;
  lowerTick?: number;
  upperPrice?: string;
  alignedUpperPrice?: string;
  upperTick?: number;
  stepRatio?: number;
}>('tradeForm/setScaleLimitPrice');

// adjust margin form only
export const setTransferAmount = createAction<{
  chainId: number;
  transferIn?: string;
  transferOut?: string;
}>('tradeForm/setTransferAmount');

// cancel order and cancel TPSL form
export const selectOrderToCancel = createAction<{
  chainId: number;
  userAddr: string;
  ids: string[];
}>('tradeForm/selectOrderOrTpslToCancel');

// trade

export const updateSimulateError = createAction<{
  type: TRADE_TYPE;
  chainId: number;
  errMessage: string;
}>('tradeForm/updateSimulateError');

export const setIsChosenOrder = createAction<{ chainId: number; isChosenOrder: boolean }>('trade/setIsChosenOrder');
export const setPnlShare = createAction<IPnlParams>('trade/setPnlShare');

// Add this helper function outside the thunk
async function fetchInstrumentInquire(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  base: string,
  isInverse: boolean,
  tradeSide: Side,
) {
  return queryClient.fetchQuery({
    queryKey: QUERY_KEYS.FUTURES.INSTRUMENT_INQUIRE(chainId, instrumentAddr, expiry, base),
    queryFn: async () => {
      if (chainId && instrumentAddr && expiry !== undefined && base !== undefined) {
        const queryBase = isInverse
          ? tradeSide === Side.LONG
            ? `-${base}`
            : base
          : tradeSide === Side.LONG
          ? base
          : `-${base}`;
        const querySize = toWad(queryBase).toString();

        const result = await fetchFuturesInstrumentInquire({
          chainId,
          instrument: instrumentAddr,
          expiry,
          size: querySize,
        });
        return result;
      }
      return null;
    },
    staleTime: 5000,
  });
}

async function fetchInstrumentInquireByTick(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  tick: number,
) {
  return queryClient.fetchQuery({
    queryKey: QUERY_KEYS.FUTURES.INSTRUMENT_INQUIRE_BY_TICK(chainId, instrumentAddr, expiry, tick),
    queryFn: async () => {
      if (chainId && instrumentAddr && expiry !== undefined && tick !== undefined) {
        const result = await fetchFuturesInstrumentInquireByTick({ chainId, instrument: instrumentAddr, expiry, tick });
        return result;
      }
      return null;
    },
    staleTime: 5000,
  });
}

export const simulateMarketTrade = createAsyncThunk(
  'tradeForm/simulate',
  async (
    {
      chainId,
      sdkContext,
      base,
      tradeSide,
      position,
      leverage,
      slippage,
      margin,
      maxLeverage = TRADE_LEVERAGE_THRESHOLDS.MAX,
      userAddr,
    }: {
      chainId: number;
      position: WrappedPosition | undefined;
      sdkContext: Context | undefined;
      base?: string | undefined;
      margin?: string;
      leverage?: string;
      tradeSide: Side;
      slippage: number;
      userAddr: string;
      maxLeverage: number;
      onlyCallFuncMode?: boolean;
      isClosePosition?: boolean;
    },
    { getState, dispatch },
  ): Promise<ISimulation | undefined> => {
    try {
      let leverageWad = leverage ? toWad(leverage) : undefined;
      if (leverageWad?.gte(toWad(maxLeverage))) {
        leverageWad = toWad(maxLeverage - TRADE_LEVERAGE_THRESHOLDS.ACTUAL_MAX_STEP);
      }
      const {
        trade: { chainMarketFormState },
      } = getState() as AppState;
      base = base || _.get(chainMarketFormState, [chainId, 'baseAmount']);

      if (sdkContext && position && base) {
        // const overallStart =
        //   typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
        if (toBN(base || 0).eq(0) || (leverageWad && leverageWad.eq(0))) {
          return;
        }
        if (position && base) {
          const baseWrap = WrappedBigNumber.from(base);
          if (
            tradeSide !== position.side &&
            baseWrap.notEq(0) &&
            baseWrap.eq(WrappedBigNumber.from(position.size).abs())
          ) {
            dispatch(
              simulateMarketClosePosition({
                chainId,
                position,
                sdkContext,
                base,
                tradeSide,
                slippage,
                userAddr,
                maxLeverage,
              }),
            );
            return;
          }
        }
        const marginWad = margin ? toWad(margin) : undefined;

        // const adjustTradeSide = getAdjustTradeSide(tradeSide, position.rootPair.isInverse);
        const adjustTradeSide = tradeSide;

        // recheck the base  after async inquire
        const {
          trade: { chainMarketFormState },
        } = getState() as AppState;
        base = base || _.get(chainMarketFormState, [chainId, 'baseAmount']);
        if (toBN(base || 0).eq(0) || (leverageWad && leverageWad.eq(0))) {
          return;
        }

        const instrumentAddr = position?.rootInstrument?.instrumentAddr;
        const expiry = position?.rootPair?.expiry;

        const quotation = await fetchInstrumentInquire(
          chainId,
          instrumentAddr,
          expiry,
          base,
          position.rootPair.isInverse,
          tradeSide,
        );

        let simulation: SimulateTradeResult;

        // const simulateStartTime =
        //   typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
        // let simulateEndTime: number;
        if (marginWad) {
          simulation = await sdkContext.perp.simulate.simulateMarketOrderByMargin({
            instrument: position.rootInstrument,
            side: adjustTradeSide,

            size: { base: toWad(base) },
            slippage: perToTenThousands(slippage),
            tradeInfo: position,
            margin: marginWad,
            isInverse: position.rootPair.isInverse,
            inquireResult: quotation
              ? {
                  quotation,
                }
              : undefined,
          });
          // simulateEndTime =
          //   typeof performance !== 'undefined' && typeof performance.now === 'function'
          //     ? performance.now()
          //     : Date.now();
        } else {
          simulation = await sdkContext.perp.simulate.simulateMarketOrderByLeverage({
            instrument: position.rootInstrument,
            leverage: leverageWad || toWad(0),
            side: tradeSide,

            size: { base: toWad(base) },
            slippage: perToTenThousands(slippage),
            tradeInfo: position,
            isInverse: position.rootPair.isInverse,
            inquireResult: quotation
              ? {
                  quotation,
                }
              : undefined,
          });
          // simulateEndTime =
          //   typeof performance !== 'undefined' && typeof performance.now === 'function'
          //     ? performance.now()
          //     : Date.now();
        }

        console.record(
          'simulate',
          `😀simulation result: [${position.rootPair.symbol}],[exceedMaxLeverage:${simulation.exceedMaxLeverage}]`,
          simulation,
          {
            position,
            leverage,
            margin,
            base,
            tradeSide,
            slippage,
          },
        );

        const gateBalanceList =
          queryClient.getQueryData<IGateValueResponse | null>(QUERY_KEYS.BALANCE.GATE(chainId, userAddr))?.portfolios ||
          [];
        const gateBalanceInfo = (gateBalanceList || [])?.find(
          (balance) => balance.address === position.rootInstrument.quoteToken.address,
        );
        const simulationResult: ISimulation = {
          chainId,
          data: {
            origin: simulation,
            ...simulation,
            simulationMainPosition: new WrappedPosition(
              {
                ...simulation.postPosition,
                id: position.rootPortfolio.id,
                instrumentId: position.rootInstrument.id,
                pairId: position.rootPair.id,
                accountId: position.rootPortfolio.rootAccount.id,
                portfolioId: position.rootPortfolio.id,
                blockInfo: position.rootPortfolio.blockInfo,
                expiry: position.rootPortfolio.expiry,
                traderAddr: position.rootPortfolio.traderAddr,
                instrumentAddr: position.rootInstrument.instrumentAddr,
              },
              position.rootPortfolio,
            ),
            tradePrice: WrappedBigNumber.from(simulation.tradePrice),
            limitTick: simulation.limitTick,
            limitPrice: WrappedBigNumber.from(TickMath.getWadAtTick(simulation.limitTick)),
            priceChangeRate: WrappedBigNumber.from(simulation.priceImpact),
            marginToDeposit: calcMarginToDeposit(simulation.margin, gateBalanceInfo?.balance || 0),
            additionalFee: WrappedBigNumber.from(simulation.stabilityFee),
            estimatedTradeValue: WrappedBigNumber.from(simulation.tradeValue),
            tradingFee: WrappedBigNumber.from(simulation.tradingFee),
            // margin may be input
            margin: WrappedBigNumber.from(simulation.marginChanged),
            realized: WrappedBigNumber.from(simulation.realized),
            minTradeValue: WrappedBigNumber.from(position.rootInstrument.minTradeValue),
            leverage: WrappedBigNumber.from(simulation.leverage),
            exceedMaxLeverage:
              simulation.exceedMaxLeverage && WrappedBigNumber.from(simulation.leverage).lt(leverage || 0),
          },
        };
        if (position.rootPair.isInverse && simulationResult?.data) {
          // simulationResult.data.tradePrice = toReciprocalNumber(simulationResult.data.tradePrice);
          // simulationResult.data.limitPrice = toReciprocalNumber(simulationResult.data.limitPrice);
          // simulationResult.data.priceChangeRate = simulationResult.data.priceChangeRate.mul(-1);
          // simulationResult.data.limitTradeValue = toReciprocalNumber(simulationResult.data.limitTradeValue);
        }
        // clear simulation display and show error
        if (simulationResult.data?.additionalFee.gt(0)) {
          simulationResult.message = 'errors.trade.additionalFeeError';
          simulationResult.data = undefined;
          return simulationResult;
        }
        // check if the margin is not enough
        if (userAddr && simulationResult.data?.marginToDeposit?.gt(0)) {
          const rootInstrument = position.rootInstrument;
          const balances = queryClient.getQueryData<ITokenBalanceFromApiResponse>(
            QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
          );
          const balance = balances?.find(
            (b) => b.address.toLowerCase() === rootInstrument.quoteToken.address.toLowerCase(),
          );
          if (balance) {
            if (simulationResult.data.marginToDeposit.gt(balance?.balance || 0)) {
              simulationResult.message = ERROR_MSG_EXCEED_WALLET_BALANCE.errorMsg;
              simulationResult.errorData = ERROR_MSG_EXCEED_WALLET_BALANCE.errorData;
            }
          }
        }

        // const overallEnd =
        //   typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
        // const simulateDuration = simulateEndTime - simulateStartTime;
        // const totalDuration = overallEnd - overallStart;
        // console.log(
        //   `⏱️ simulateMarketTrade time cost: total=${totalDuration.toFixed(2)}ms, simulate=${simulateDuration.toFixed(
        //     2,
        //   )}ms`,
        // );
        return simulationResult;
      }
      return;
    } catch (e) {
      SentryService.captureException(
        e,
        {
          name: 'action:tradeForm/simulate',
          chainId,
          base,
          tradeSide,
          position: position?.toJSON(),
          leverage,
          slippage,
          margin,
          maxLeverage,
          userAddr,
        },
        'info',
      );
      console.error('🚀 simulate error', e);
      if (e instanceof AxiosError) {
        return { chainId, message: e.response?.data?.msg || e.response?.data?.message || e.message };
      }
      const parsedError = parsedEthersError(e);
      const errorMsg = parsedError?.errorMsg || _.get(e, ['reason']) || _.get(e, ['message']);
      return { chainId, message: parsedError?.errorData || errorMsg, errorData: parsedError?.errorData };
    }
  },
);

export const simulateMarketClosePosition = createAsyncThunk(
  'tradeForm/simulateMarketClosePosition',
  async (
    {
      chainId,
      sdkContext,
      base,
      tradeSide,
      position,
      slippage,
      userAddr,
    }: {
      chainId: number;
      position: WrappedPosition | undefined;
      sdkContext: Context | undefined;
      base?: string | undefined;
      tradeSide: Side;
      slippage: number;
      userAddr: string;
      maxLeverage: number;
      onlyCallFuncMode?: boolean;
      isClosePosition?: boolean;
    },
    { getState },
  ): Promise<ISimulation | undefined> => {
    try {
      const {
        trade: { chainMarketFormState },
      } = getState() as AppState;
      base = base || _.get(chainMarketFormState, [chainId, 'baseAmount']);
      if (sdkContext && position && base) {
        // recheck the base  after async inquire
        const {
          trade: { chainMarketFormState },
        } = getState() as AppState;
        base = base || _.get(chainMarketFormState, [chainId, 'baseAmount']);

        const instrumentAddr = position.rootInstrument.instrumentAddr;
        const expiry = position.rootPortfolio.expiry;
        const quotation = await fetchInstrumentInquire(
          chainId,
          instrumentAddr,
          expiry,
          base,
          position.rootPair.isInverse,
          tradeSide,
        );

        const simulation: SimulateTradeResult = await sdkContext.perp.simulate.simulateClose({
          instrument: position.rootInstrument,

          size: { base: toWad(base) },
          slippage: perToTenThousands(slippage),
          tradeInfo: position,
          isInverse: position.rootPair.isInverse,
          inquireResult: quotation
            ? {
                quotation,
              }
            : undefined,
        });

        console.log(
          `😀simulation result: [${position.rootPair.symbol}],[exceedMaxLeverage:${simulation.exceedMaxLeverage}]`,
          simulation,
          {
            position,
            base,
            tradeSide,
            slippage,
          },
        );

        const gateBalanceList =
          queryClient.getQueryData<IGateValueResponse | null>(QUERY_KEYS.BALANCE.GATE(chainId, userAddr))?.portfolios ||
          [];
        const gateBalanceInfo = (gateBalanceList || [])?.find(
          (balance) => balance.address === position.rootInstrument.quoteToken.address,
        );

        const simulationResult: ISimulation = {
          chainId,
          data: {
            origin: simulation,
            ...simulation,
            simulationMainPosition: new WrappedPosition(
              {
                ...simulation.postPosition,
                id: position.rootPortfolio.id,
                instrumentId: position.rootInstrument.id,
                pairId: position.rootPair.id,
                accountId: position.rootPortfolio.rootAccount.id,
                portfolioId: position.rootPortfolio.id,
                blockInfo: position.rootPortfolio.blockInfo,
                expiry: position.rootPortfolio.expiry,
                traderAddr: position.rootPortfolio.traderAddr,
                instrumentAddr: position.rootInstrument.instrumentAddr,
              },
              position.rootPortfolio,
            ),
            tradePrice: WrappedBigNumber.from(simulation.tradePrice),
            limitTick: simulation.limitTick,
            limitPrice: WrappedBigNumber.from(TickMath.getWadAtTick(simulation.limitTick)),
            priceChangeRate: WrappedBigNumber.from(simulation.priceImpact),
            marginToDeposit: calcMarginToDeposit(simulation.margin, gateBalanceInfo?.balance || 0),
            additionalFee: WrappedBigNumber.from(simulation.stabilityFee),
            estimatedTradeValue: WrappedBigNumber.from(simulation.tradeValue),
            tradingFee: WrappedBigNumber.from(simulation.tradingFee),
            // margin may be input
            margin: WrappedBigNumber.from(simulation.marginChanged),
            realized: WrappedBigNumber.from(simulation.realized),
            minTradeValue: WrappedBigNumber.from(position.rootInstrument.minTradeValue),
            leverage: WrappedBigNumber.from(simulation.leverage),
            exceedMaxLeverage: false,
          },
        };
        if (position.rootPair.isInverse && simulationResult?.data) {
          // simulationResult.data.tradePrice = toReciprocalNumber(simulationResult.data.tradePrice);
          // simulationResult.data.limitPrice = toReciprocalNumber(simulationResult.data.limitPrice);
          // simulationResult.data.priceChangeRate = simulationResult.data.priceChangeRate.mul(-1);
          // simulationResult.data.limitTradeValue = toReciprocalNumber(simulationResult.data.limitTradeValue);
        }

        // check if the margin is not enough
        if (userAddr && simulationResult.data?.marginToDeposit?.gt(0)) {
          const rootInstrument = position.rootInstrument;
          const balances = queryClient.getQueryData<ITokenBalanceFromApiResponse>(
            QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
          );
          const balance = balances?.find(
            (b) => b.address.toLowerCase() === rootInstrument.quoteToken.address.toLowerCase(),
          );
          if (balance) {
            if (simulationResult.data.marginToDeposit.gt(balance?.balance || 0)) {
              simulationResult.message = ERROR_MSG_EXCEED_WALLET_BALANCE.errorMsg;
              simulationResult.errorData = ERROR_MSG_EXCEED_WALLET_BALANCE.errorData;
            }
          }
        }

        return simulationResult;
      }
      return;
    } catch (e) {
      SentryService.captureException(
        e,
        {
          name: 'action:tradeForm/simulateMarketClosePosition',
          chainId,
          base,
          tradeSide,
          position: position?.toJSON(),
          slippage,
          userAddr,
        },
        'info',
      );

      console.error('🚀 simulate error', e);
      const parsedError = parsedEthersError(e);
      const errorMsg = parsedError?.errorMsg || _.get(e, ['reason']) || _.get(e, ['message']);
      return { chainId, message: parsedError?.errorData || errorMsg, errorData: parsedError?.errorData };
    }
  },
);

export const trade = createAsyncThunk(
  'tradeForm/trade',
  async (
    {
      signer,
      chainId,
      userAddr,
      sdk: sdk,
      pair,
      baseAmount,
      side,
      slippage,
      deadline,
      provider,
      tradePrice,
      simulation,
      sendTxNotification,
    }: {
      chainId: number;
      userAddr: string;
      signer: JsonRpcSigner;
      sdk: Context;
      pair: WrappedPair;
      side: Side;
      baseAmount: BigNumber;
      slippage: number;
      deadline: number;
      simulation?: ISimulation;
      provider?: JsonRpcProvider;
      tradePrice: WrappedBigNumber;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch, getState },
  ): Promise<ContractReceipt | undefined> => {
    try {
      if (!simulation || !simulation?.data) return;
      // const adjustTradeSide = getAdjustTradeSide(side, pair.isInverse);
      // const adjustTradeSide = side;
      const sendingTemplate = parseSendingTxMessageMapping[OPERATION_TX_TYPE.TRADE]!(
        baseAmount,
        side,
        simulation?.data?.limitPrice || WrappedBigNumber.ZERO,
        pair.rootInstrument.baseToken.symbol,
        pair.rootInstrument.isInverse,
      );
      const result = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record(
              'tx',
              `Try to send [${OPERATION_TX_TYPE.TRADE}] operation tx for pair [${pair.symbol}]`,
              undefined,
              {
                signer,
                expiry: pair.expiry,
                pair: pair,
                futures: pair.rootInstrument,
                amount: formatEther(baseAmount),
                margin: formatEther(simulation?.data?.margin?.wadValue),
                slippage: perToTenThousands(slippage),
                deadline: addedDeadline(deadline),
                side: side,
                tradePrice,
                simulation,
              },
            );
            const {
              global: { onChainReferralCode },
            } = getState() as AppState;
            const referralCode = onChainReferralCode?.referralCode || '';

            const params: PlaceMarketOrderParam = {
              instrumentAddr: pair.rootInstrument.instrumentAddr,
              expiry: pair.expiry,
              // side: adjustTradeSide,
              side: getAdjustTradeSide(side, pair.isInverse),
              baseSize: baseAmount,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
              margin: simulation?.data?.origin.margin!,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
              limitTick: simulation?.data?.origin.limitTick!,
              deadline: addedDeadline(deadline),
              referralCode,
              isInverse: pair.isInverse,
            };

            const instrumentAddr = params.instrumentAddr;

            const txType = OPERATION_TX_TYPE.TRADE;

            return handleSendAgentOrNormalTx({
              signer,
              provider,
              userAddr,
              chainId,
              instrumentAddr,
              sendingTemplate,
              sendTxNotification,
              txType,
              sdk,
              handlePopulatedTx: async () => {
                const populatedTx = await sdk.perp.instrument.placeMarketOrder(params, { from: userAddr });
                return populatedTx;
              },
              handleGelatoTxTargetData: async () => {
                const instrumentContract = getSynContract({
                  chainId,
                  type: 'instrument',
                  signerOrProvider: signer,
                  address: instrumentAddr,
                });
                const sign = utils.signOfSide(params.side);
                const tradeParam: PlaceParam = {
                  expiry: params.expiry,
                  size: params.baseSize.mul(sign),
                  amount: params.margin,
                  tick: params.limitTick,
                  deadline: params.deadline,
                  referralCode: params.referralCode ?? DEFAULT_REFERRAL_CODE,
                };
                const txArgs = utils.encodePlaceParam(tradeParam);
                const targetData = instrumentContract.interface.encodeFunctionData('tradeFor', [userAddr, txArgs]);
                return targetData;
              },
            });
          },

          chainId,
          userAddr,
          txParams: {
            type: OPERATION_TX_TYPE.TRADE,
            instrument: {
              baseSymbol: pair.rootInstrument.baseToken.symbol,
              quoteSymbol: pair.rootInstrument.quoteToken.symbol,
              isInverse: pair.rootInstrument.isInverse,
            },
            sendingTemplate: sendingTemplate,
          },
          wssProvider: provider,
        }),
      ).unwrap();
      if (result?.status) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, pair.rootInstrument.instrumentAddr, pair.expiry),
        });
        // need update balance
        if (simulation?.data?.margin?.notEq(0)) {
          if (simulation.data.marginToDeposit.gt(0)) {
            dispatch(
              checkTokenAllowance({
                userAddress: userAddr,
                chainId,
                marginToken: pair.rootInstrument.quoteToken,
                block: result.blockNumber,
                sdk: sdk,
              }),
            );

            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
            });
          }
          dispatch(
            fetchGateBalanceAction({
              chainId,
              userAddr,
              tokens: [pair.rootInstrument.quoteToken],
              block: result.blockNumber,
            }),
          );
        }
      }
      if (result?.transactionHash) {
        const { setPollingHistoryTx } = useGlobalStore.getState();
        setPollingHistoryTx({
          userAddress: userAddr,
          chainId,
          pollingHistoryId: PollingHistoryId.trade,
          tx: result.transactionHash,
        });
      }
      return result;
    } catch (e) {
      SentryService.captureException(e, {
        name: 'action:tradeForm/trade',
        chainId,
        userAddr,
        pair: pair?.toJSON(),
        baseAmount: baseAmount?.toJSON(),
        side,
        slippage,
        deadline,
        tradePrice: tradePrice?.stringValue,
      });
      console.error('trade error', e);
      throw e;
    }
  },
);

export const placeLimitOrder = createAsyncThunk(
  'tradeForm/limitOrder',
  async (
    {
      signer,
      chainId,
      userAddr,
      sdkContext,
      pair,
      deadline,
      simulation,
      provider,
      side,
      sendTxNotification,
    }: {
      chainId: number;
      userAddr: string;
      signer: JsonRpcSigner;
      pair: WrappedPair;
      sdkContext: Context;
      deadline: number;
      simulation: IOrderSimulationData;
      provider?: JsonRpcProvider;
      side: Side;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch, getState },
  ): Promise<ContractReceipt | undefined> => {
    try {
      // const adjustTradeSide = side; // getAdjustTradeSide(side, pair.isInverse);

      const sendingTemplate = parseSendingTxMessageMapping[OPERATION_TX_TYPE.PLACE_ORDER]!(
        pair.rootInstrument.baseToken.symbol,
        simulation.tickNumber,
        simulation.size.base,
        pair.rootInstrument.isInverse,
        side,
      );
      const result = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record(
              'tx',
              `Try to send [${OPERATION_TX_TYPE.PLACE_ORDER}] operation tx for pair [${
                pair.rootInstrument.symbol
              }-${showProperDateString({ expiry: pair.expiry, format: 'MMDD' })}]`,
              undefined,
              {
                signer,
                expiry: pair.expiry,
                pair: pair,
                futures: pair.rootInstrument,
                size: simulation.size,
                balance: formatEther(BigNumber.from(simulation.margin.wadValue)),
                leverage: formatEther(simulation.leverage),
                deadline: addedDeadline(deadline),
              },
            );
            const {
              global: { onChainReferralCode },
            } = getState() as AppState;
            const referralCode = onChainReferralCode?.referralCode || '';
            const params = {
              instrumentAddr: pair.instrumentAddr,
              expiry: pair.expiry,
              tick: simulation.tickNumber,
              baseSize: simulation.size.base,
              margin: simulation.margin.wadValue,
              // side: adjustTradeSide,
              side: getAdjustTradeSide(side, pair.isInverse),
              deadline: addedDeadline(deadline),
              referralCode,
              isInverse: pair.isInverse,
            };
            const instrumentAddr = pair.rootInstrument.instrumentAddr;
            const txType = OPERATION_TX_TYPE.PLACE_ORDER;

            return handleSendAgentOrNormalTx({
              signer,
              provider,
              userAddr,
              chainId,
              instrumentAddr,
              sendingTemplate,
              sendTxNotification,
              txType,
              sdk: sdkContext,
              handlePopulatedTx: async () => {
                const populatedTx = await sdkContext.perp.instrument.placeLimitOrder(params, { from: userAddr });
                return populatedTx;
              },
              handleGelatoTxTargetData: async () => {
                const instrumentContract = getSynContract({
                  chainId,
                  type: 'instrument',
                  signerOrProvider: signer,
                  address: instrumentAddr,
                });

                const sign = utils.signOfSide(params.side);

                const placeParam: PlaceParam = {
                  expiry: params.expiry,
                  size: params.baseSize.mul(sign),
                  amount: params.margin,
                  tick: params.tick,
                  deadline: params.deadline,
                };

                const txArgs = utils.encodePlaceParam(placeParam);

                const targetData = instrumentContract.interface.encodeFunctionData('placeFor', [userAddr, txArgs]);
                return targetData;
              },
            });
          },

          chainId,
          userAddr,
          txParams: {
            type: OPERATION_TX_TYPE.PLACE_ORDER,
            instrument: {
              baseSymbol: pair.rootInstrument.baseToken.symbol,
              quoteSymbol: pair.rootInstrument.quoteToken.symbol,
              isInverse: pair.rootInstrument.isInverse,
            },
            sendingTemplate: sendingTemplate,
          },
          wssProvider: provider,
        }),
      ).unwrap();
      if (result?.status) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, pair.rootInstrument.instrumentAddr, pair.expiry),
        });
        // need update balance
        if (simulation?.margin?.gt(0)) {
          if (simulation.marginToDeposit.gt(0)) {
            dispatch(
              checkTokenAllowance({
                userAddress: userAddr,
                chainId,
                marginToken: pair.rootInstrument.quoteToken,
                block: result.blockNumber,
                sdk: sdkContext,
              }),
            );

            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
            });
          }
          if (!simulation?.margin.eq(simulation?.marginToDeposit)) {
            dispatch(
              fetchGateBalanceAction({
                chainId,
                userAddr,
                tokens: [pair.rootInstrument.quoteToken],
                block: result.blockNumber,
              }),
            );
          }
        }
      }
      return result;
    } catch (e) {
      SentryService.captureException(e, {
        name: 'action:tradeForm/limitOrder',
        chainId,
        userAddr,
        pair: pair?.toJSON(),
        deadline,
        side,
      });
      console.log('🚀 ~ file: actions.ts:125 ~ e:', e);
      throw e;
    }
  },
);

export const orderSimulate = createAsyncThunk(
  'tradeForm/orderSimulate',
  async (
    {
      sdkContext,
      pair,
      userAddr,
      base,
      limitPrice,
      tradeSide,
      leverage,
      chainId,
    }: {
      chainId: number;
      limitPrice: string | undefined;
      pair: WrappedPair | undefined;
      userAddr: string | undefined;
      sdkContext: Context;
      base: BigNumber;
      leverage?: BigNumber;
      tradeSide: Side;
      onlyCallFuncMode?: boolean;
    },
    { getState },
  ): Promise<IOrderSimulation | undefined> => {
    try {
      const {
        account: { chainMetaOrder },
        trade: { chainLimitFormStatus },
      } = getState() as AppState;
      const limitFormStatus = _.get(chainLimitFormStatus, [chainId]);
      if (
        limitPrice &&
        !pair?.wrapAttribute('fairPrice').eq(limitPrice) &&
        !base.eq(0) &&
        !(leverage && leverage.eq(0)) &&
        // when a placing order happens the portfolio may update before the placeLimitOrder fullfil,
        // this is to prevent resimulation in such situation
        limitFormStatus !== FETCHING_STATUS.FETCHING
      ) {
        if (pair) {
          const rootInstrument = pair.rootInstrument;

          const limitRes = getLimitAlignPriceWad({
            limitPrice,
            isInverse: rootInstrument.isInverse,
          });
          if (!limitRes) return;
          const { tick: tickNumber } = limitRes;

          console.log('🚀 ~ tickNumber check:', {
            inputPriceToTick: tickNumber,
            inputPrice: limitPrice,
            tickToPrice: WrappedBigNumber.from(TickMath.getWadAtTick(tickNumber)).stringValue,
          });

          // use chainMetaOrder for best accuracy
          const orderList = _.values(
            _.pickBy(
              _.get(chainMetaOrder, [chainId || '', userAddr || '', 'list']),
              (metaOrder) => metaOrder.pairId === pair.id,
            ),
          );
          if (orderList.length) {
            // orderList
            const placedOrder = orderList.find((order: IMetaOrder) => order.tick === tickNumber);

            if (placedOrder) {
              return { message: ERROR_MSG_ORDER_OCCUPIED.errorMsg, errorData: ERROR_MSG_ORDER_OCCUPIED.errorData };
            }
          }

          if (!leverage) return;

          const result = await sdkContext.perp.simulate.simulateLimitOrder({
            instrument: rootInstrument,
            leverage: leverage,
            side: tradeSide,
            size: { base: base },
            tradeInfo: {
              instrumentAddr: pair.rootInstrument.instrumentAddr,
              expiry: pair.expiry,
              traderAddr: userAddr || '',
            },
            priceInfo: tickNumber,
            isInverse: rootInstrument.isInverse,
          });
          const estimatedTradeValue = WrappedBigNumber.from(TickMath.getWadAtTick(tickNumber)).mul(base);

          const gateBalanceList =
            queryClient.getQueryData<IGateValueResponse | null>(QUERY_KEYS.BALANCE.GATE(chainId, userAddr))
              ?.portfolios || [];
          const gateBalanceInfo = (gateBalanceList || [])?.find(
            (balance) => balance.address === rootInstrument.quoteToken.address,
          );

          const simulateResult: IOrderSimulation = {
            data: {
              origin: result,
              ...result,
              tickNumber,
              // limitPrice: WrappedBigNumber.from(
              //   await sdkContext.perp.calc.getWadAtTick(portfolio.instrumentAddr, tickNumber),
              // ),
              limitPrice: getAdjustTradePrice(
                WrappedBigNumber.from(TickMath.getWadAtTick(tickNumber)),
                !!rootInstrument?.isInverse,
              ),
              marginToDeposit: calcMarginToDeposit(result.margin, gateBalanceInfo?.balance || 0),
              margin: WrappedBigNumber.from(result.margin),
              minFeeRebate: WrappedBigNumber.from(result.minFeeRebate),
              estimatedTradeValue: estimatedTradeValue,
              leverage: result.leverage,
              minOrderValue: rootInstrument.minOrderValue,
              baseSize: result.size.base,
            },
          };

          // check if the margin is not enough
          if (userAddr && simulateResult.data?.marginToDeposit?.gt(0)) {
            const balances = queryClient.getQueryData<ITokenBalanceFromApiResponse>(
              QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
            );
            const balance = balances?.find(
              (b) => b.address.toLowerCase() === rootInstrument.quoteToken.address.toLowerCase(),
            );
            if (balance) {
              if (simulateResult.data.marginToDeposit.gt(balance?.balance || 0)) {
                simulateResult.message = ERROR_MSG_EXCEED_WALLET_BALANCE.errorMsg;
                simulateResult.errorData = ERROR_MSG_EXCEED_WALLET_BALANCE.errorData;
              }
            }
          }
          console.log('🚀 ~ simulate points:', {
            chainId,
            tick: tickNumber,
            base,
            quote: rootInstrument.quoteToken,
            pair,
            userAddr: userAddr || '',
          });

          return simulateResult;
        }
      }
      return undefined;
    } catch (e) {
      SentryService.captureException(
        e,
        {
          name: 'action:tradeForm/orderSimulate',
          base: JSON.stringify(base),
          limitPrice,
          tradeSide,
          leverage,
          chainId,
        },
        'info',
      );
      console.log('🚀 orderSimulate error', e);
      const parsedError = parsedEthersError(e);
      const errorMsg = parsedError?.errorMsg || _.get(e, ['reason']) || _.get(e, ['message']);
      console.log('🚀 ~ file: actions.ts:546 ~ errorMsg:', errorMsg, parsedError);
      if (errorMsg.startsWith(ERROR_MSG_FAIR_FAR_FROM_MARK)) {
        return { message: getMappedRevertReason('0xe732e534'), errorData: parsedError?.errorData };
      }
      // if (errorMsg.startsWith(ERROR_MSG_LIMIT_PRICE_FAR_FROM_MARK) || errorMsg.includes(INVALID_PRICE)) {
      //   return;
      // }
      return { message: errorMsg, errorData: parsedError?.errorData };
    }
  },
);

export const adjustMargin = createAsyncThunk(
  'tradeForm/adjustPositionMargin',
  async (
    {
      sdkContext,
      chainId,
      userAddr,
      signer,
      pair,
      transferIn,
      deadline,
      margin,
      provider,
      sendTxNotification,
    }: {
      chainId: number;
      userAddr: string;
      sdkContext: Context;
      signer: JsonRpcSigner;
      transferIn: boolean;
      pair: WrappedPair;
      deadline: number;
      margin: BigNumber;
      provider: JsonRpcProvider;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch, getState },
  ): Promise<ContractReceipt | undefined> => {
    try {
      const {
        trade: { chainAdjustMarginSimulation },
      } = getState() as AppState;
      const simulation = _.get(chainAdjustMarginSimulation, [chainId]);
      const sendingTemplate = parseSendingTxMessageMapping[OPERATION_TX_TYPE.ADJUST_MARGIN]!(
        margin,
        transferIn,
        pair.rootInstrument.quoteToken.symbol,
      );
      const result = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record(
              'tx',
              `Try to send [${OPERATION_TX_TYPE.ADJUST_MARGIN}] operation tx for pair [${
                pair.rootInstrument.symbol
              }-${showProperDateString({ expiry: pair.expiry, format: 'MMDD' })}]`,
              undefined,
              {
                signer,
                expiry: pair.expiry,
                pair: pair,
                futures: pair.rootInstrument,
                transferIn,
                margin: formatEther(margin),
                deadline: addedDeadline(deadline),
              },
            );
            const {
              global: { onChainReferralCode },
            } = getState() as AppState;
            const referralCode = onChainReferralCode?.referralCode || '';
            const params = {
              instrumentAddr: pair.rootInstrument.instrumentAddr,
              expiry: pair.expiry,
              margin: margin,
              transferIn: transferIn,
              deadline: addedDeadline(deadline),
              referralCode,
            };

            const instrumentAddr = pair.rootInstrument.instrumentAddr;
            const txType = OPERATION_TX_TYPE.ADJUST_MARGIN;

            return handleSendAgentOrNormalTx({
              signer,
              userAddr,
              chainId,
              instrumentAddr,
              sendingTemplate,
              sendTxNotification,
              txType,
              sdk: sdkContext,
              provider,
              handlePopulatedTx: async () => {
                const populatedTx = await sdkContext.perp.instrument.adjustMargin(params, { from: userAddr });
                return populatedTx;
              },
              handleGelatoTxTargetData: async () => {
                const instrumentContract = getSynContract({
                  chainId,
                  type: 'instrument',
                  signerOrProvider: signer,
                  address: instrumentAddr,
                });

                const sign: number = params.transferIn ? 1 : -1;

                const adjustParam = {
                  expiry: params.expiry,
                  net: params.margin.mul(sign),
                  deadline: params.deadline,
                  referralCode: params.referralCode ?? DEFAULT_REFERRAL_CODE,
                };

                const txArgs = utils.encodeAdjustParam(adjustParam);

                const targetData = instrumentContract.interface.encodeFunctionData('tradeFor', [userAddr, txArgs]);
                return targetData;
              },
            });
          },

          chainId,
          userAddr,
          txParams: {
            type: OPERATION_TX_TYPE.ADJUST_MARGIN,
            instrument: {
              baseSymbol: pair.rootInstrument.baseToken.symbol,
              quoteSymbol: pair.rootInstrument.quoteToken.symbol,
              isInverse: pair.rootInstrument.isInverse,
            },
            sendingTemplate: sendingTemplate,
          },
          wssProvider: provider,
        }),
      ).unwrap();
      if (result?.status) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, pair.rootInstrument.instrumentAddr, pair.expiry),
        });
        // need update balance
        if (margin.gt(0)) {
          if (
            simulation?.data?.marginToDepositWad &&
            WrappedBigNumber.from(simulation?.data?.marginToDepositWad).gt(0)
          ) {
            dispatch(
              checkTokenAllowance({
                userAddress: userAddr,
                chainId,
                marginToken: pair.rootInstrument.quoteToken,
                block: result.blockNumber,
                sdk: sdkContext,
              }),
            );

            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
            });
          }
          if (simulation?.data?.marginToDepositWad && !margin.eq(simulation?.data?.marginToDepositWad || 0)) {
            dispatch(
              fetchGateBalanceAction({
                chainId,
                userAddr,
                tokens: [pair.rootInstrument.quoteToken],
                block: result.blockNumber,
              }),
            );
          }
        }
        // reset form after success
        dispatch(resetAdjustMarginForm({ chainId }));
        dispatch(switchToMarketTab({ chainId }));
      }
      if (result?.transactionHash) {
        const { setPollingHistoryTx } = useGlobalStore.getState();
        setPollingHistoryTx({
          chainId,
          userAddress: userAddr,
          pollingHistoryId: PollingHistoryId.transfer,
          tx: result?.transactionHash,
        });
      }
      return result;
    } catch (e) {
      SentryService.captureException(e, {
        name: 'tradeForm/adjustPositionMargin',
        chainId,
        userAddr,
        pair: pair?.toJSON(),
        transferIn,
        deadline,
        margin: JSON.stringify(margin),
      });

      console.log('🚀 ~ file: actions.ts:125 ~ e:', e);
      throw e;
    }
  },
);

export const cancelTradeOrders = createAsyncThunk(
  'tradeForm/cancelTradeOrders',
  async (
    {
      sdkContext,
      chainId,
      signer,
      orders,
      userAddr,
      portfolio,
      deadline,
      provider,
      sendTxNotification,
    }: {
      chainId: number;
      sdkContext: Context;
      signer: JsonRpcSigner;
      userAddr: string;
      orders: WrappedOrder[];
      portfolio: WrappedPortfolio;
      deadline: number;
      provider: JsonRpcProvider;
      bulkCancel?: boolean;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch },
  ): Promise<ContractReceipt | undefined> => {
    try {
      const pair = portfolio.rootPair;
      let sendingTemplate: IParsedTemplate | undefined = undefined;
      if (!orders?.length) {
        return undefined;
      }
      if (orders.length === 1) {
        sendingTemplate = parseSendingTxMessageMapping[OPERATION_TX_TYPE.CANCEL_ORDER]!(orders[0].tick, pair.isInverse);
      } else if (orders.length > 1) {
        const result: IParsedTemplate = { descTemplate: '', templateArgs: {} };
        result.descTemplate = 'notification.cancelOrder.multiInfo';
        result.templateArgs = {
          orderNumber: orders.length.toString(),
        };
        sendingTemplate = result;
      }
      const result = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record(
              'tx',
              `Try to send [${OPERATION_TX_TYPE.CANCEL_ORDER}] operation tx for pair [${portfolio.rootInstrument.symbol}`,
              undefined,
              {
                signer,
                portfolio,
                orders,
                deadline: addedDeadline(deadline),
              },
            );
            const params = {
              instrumentAddr: pair.rootInstrument.instrumentAddr,
              expiry: pair.expiry,
              orderTicks: orders.map((order) => order.tick),
              deadline: addedDeadline(deadline),
              isInverse: pair.isInverse,
            };

            const instrumentAddr = pair.rootInstrument.instrumentAddr;
            const txType = OPERATION_TX_TYPE.CANCEL_ORDER;

            return handleSendAgentOrNormalTx({
              signer,
              provider,
              userAddr,
              chainId,
              instrumentAddr,
              sendingTemplate,
              sendTxNotification,
              txType,
              sdk: sdkContext,
              needBatchData: params.orderTicks.length <= MAX_CANCEL_ORDER_COUNT,
              handlePopulatedTx: async () => {
                const populatedTx = await sdkContext.perp.instrument.batchCancelLimitOrder(params, { from: userAddr });
                return populatedTx;
              },
              handleGelatoTxTargetData: async (agentAddr: string) => {
                const instrumentContract = getSynContract({
                  chainId,
                  type: 'instrument',
                  signerOrProvider: signer,
                  address: instrumentAddr,
                });

                if (params.orderTicks.length <= MAX_CANCEL_ORDER_COUNT) {
                  const txArgs = utils.encodeCancelParam({
                    expiry: params.expiry,
                    ticks: params.orderTicks,
                    deadline: params.deadline,
                  });
                  const targetData = instrumentContract.interface.encodeFunctionData('cancelFor', [userAddr, txArgs]);
                  return targetData;
                } else {
                  // split ticks by size of MAX_CANCEL_ORDER_COUNT
                  const tickGroups = [];
                  const instrumentAddrs = [];
                  for (let i = 0; i < params.orderTicks.length; i += MAX_CANCEL_ORDER_COUNT) {
                    tickGroups.push(params.orderTicks.slice(i, i + MAX_CANCEL_ORDER_COUNT));
                    instrumentAddrs.push(instrumentAddr);
                  }
                  const calldatas = tickGroups.map((group) => {
                    const txArgs = utils.encodeCancelParam({
                      expiry: params.expiry,
                      ticks: group,
                      deadline: params.deadline,
                    });
                    return instrumentContract.interface.encodeFunctionData('cancelFor', [userAddr, txArgs]);
                    // return instrumentContract.interface.encodeFunctionData('cancel', [txArgs]);
                  });

                  const routerContract = getSynContract({
                    chainId,
                    type: 'router',
                    signerOrProvider: signer,
                  });

                  const targetData = routerContract.interface.encodeFunctionData('batchMulticall', [
                    agentAddr, // signer address
                    userAddr,
                    instrumentAddrs, // targetContract address
                    calldatas,
                    false, // userPaid
                  ]);

                  return targetData;
                }
              },
            });
          },

          chainId,
          userAddr,
          txParams: {
            type: OPERATION_TX_TYPE.CANCEL_ORDER,
            instrument: {
              baseSymbol: pair.rootInstrument.baseToken.symbol,
              quoteSymbol: pair.rootInstrument.quoteToken.symbol,
              isInverse: pair.rootInstrument.isInverse,
            },
            sendingTemplate: sendingTemplate,
          },
          wssProvider: provider,
        }),
      ).unwrap();
      console.log('🚀 ~ result:', result);
      if (result?.status) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, pair.rootInstrument.instrumentAddr, pair.expiry),
        });
        dispatch(
          fetchGateBalanceAction({
            chainId,
            userAddr,
            tokens: [pair.rootInstrument.quoteToken],
            block: result.blockNumber,
          }),
        );
        if (orders.length) {
          orders.forEach((order) => {
            _.unset(portfolio.orderMap, [order.id]);
          });
        }
      }
      if (result?.transactionHash) {
        const { setPollingHistoryTx } = useGlobalStore.getState();
        setPollingHistoryTx({
          chainId,
          userAddress: userAddr,
          pollingHistoryId: PollingHistoryId.order,
          tx: result?.transactionHash,
        });
      }
      return result;
    } catch (e) {
      SentryService.captureException(e, {
        name: 'tradeForm/cancelTradeOrders',
        chainId,
        orders: orders?.map((order) => order.id),
        userAddr,
        deadline,
      });
      console.log('🚀 ~ file: actions.ts:125 ~ e:', e);
      throw e;
    }
  },
);

export const simulateAdjustMargin = createAsyncThunk(
  'trade/simulateAdjustMargin',
  async ({
    chainId,
    sdkContext,
    pair,
    portfolio,
    leverage,
    transferAmount,
    transferIn,
    slippage,
  }: {
    chainId: number;
    pair: WrappedPair | undefined;
    portfolio: WrappedPortfolio | undefined;
    sdkContext: Context | undefined;
    transferAmount: BigNumber | undefined;
    leverage: BigNumber | undefined;
    transferIn: boolean;
    slippage: number;
  }): Promise<IAdjustMarginSimulation | undefined> => {
    try {
      if (sdkContext && portfolio && pair) {
        if (portfolio.position.balance.eq(0) || transferAmount?.eq(0)) {
          return;
        }

        const gateBalanceList =
          queryClient.getQueryData<IGateValueResponse | null>(QUERY_KEYS.BALANCE.GATE(chainId, portfolio.traderAddr))
            ?.portfolios || [];
        const gateBalanceInfo = (gateBalanceList || [])?.find(
          (balance) => balance.address === pair.rootInstrument.quoteToken.address,
        );

        if (transferAmount) {
          const simulationRes = await sdkContext?.perp.simulate.simulateAdjustMarginByMargin({
            transferIn,
            tradeInfo: portfolio.position,
            instrument: portfolio.rootInstrument,
            margin: transferAmount,
            slippage: perToTenThousands(slippage),
            isInverse: portfolio.rootPair.isInverse,
          });

          const res = {
            chainId,
            data: {
              ...simulationRes,
              simulationMainPosition: new WrappedPosition(
                {
                  ...simulationRes.postPosition,
                  id: portfolio.id,
                  instrumentId: pair.rootInstrument.id,
                  pairId: pair.id,
                  accountId: portfolio.rootAccount.id,
                  portfolioId: portfolio.id,
                  blockInfo: portfolio.blockInfo!,
                },
                portfolio,
              ),
              transferAmount: transferAmount.mul(transferIn ? 1 : -1),
              leverageWad: simulationRes.leverage,
              marginToDepositWad: calcMarginToDeposit(
                transferAmount.mul(transferIn ? 1 : -1),
                gateBalanceInfo?.balance || 0,
              ).wadValue,
            },
          };
          return res;
        } else {
          const simulationRes = await sdkContext?.perp.simulate.simulateAdjustMarginByLeverage({
            leverage: leverage!,
            tradeInfo: portfolio.position,
            slippage: perToTenThousands(slippage),
            isInverse: portfolio.rootPair.isInverse,
            instrument: portfolio.rootInstrument,
          });

          const res = {
            chainId,
            data: {
              ...simulationRes,
              simulationMainPosition: new WrappedPosition(
                {
                  ...simulationRes.postPosition,
                  id: portfolio.id,
                  instrumentId: pair.rootInstrument.id,
                  pairId: pair.id,
                  accountId: portfolio.rootAccount.id,
                  portfolioId: portfolio.id,
                  blockInfo: portfolio.blockInfo!,
                },
                portfolio,
              ),
              transferAmount: simulationRes.margin.mul(simulationRes.transferIn ? 1 : -1),
              leverageWad: leverage!,
              marginToDepositWad: calcMarginToDeposit(simulationRes.margin, gateBalanceInfo?.balance || 0).wadValue,
            },
          };

          return res;
        }
      }
      return { chainId };
    } catch (e) {
      SentryService.captureException(
        e,
        {
          name: 'tradeForm/simulateAdjustMargin',
          chainId,
          pair: pair?.toJSON(),
          leverage: JSON.stringify(leverage),
          transferAmount: JSON.stringify(transferAmount),
          transferIn,
          slippage,
        },
        'info',
      );
      console.log('🚀 ~ file: actions.ts:565 ~ e:', e);
      return { chainId, message: _.get(e, ['message']) };
    }
  },
);

export const getBaseOrQuoteAmount = createAsyncThunk(
  'tradeForm/getBaseOrQuoteAmount',
  async (
    {
      sdkContext,
      chainId,
      pair,
      baseAmount,
      quoteAmount,
      tradeSide,
      tradeType,
    }: {
      chainId: number;
      sdkContext: Context | undefined;
      pair: WrappedPair | undefined;
      baseAmount: BigNumber | undefined;
      quoteAmount: BigNumber | undefined;
      tradeSide: Side;
      tradeType: TRADE_TYPE;
    },
    { dispatch, getState },
  ): Promise<{ amount: BigNumber; quotation: Quotation } | undefined> => {
    try {
      if (sdkContext && pair) {
        if (baseAmount?.eq(0) && quoteAmount?.eq(0)) {
          return;
        }
        if (quoteAmount) {
          const res = await sdkContext?.perp?.observer.inquireByQuote(
            pair.instrumentAddr,
            pair.expiry,
            tradeSide,
            quoteAmount || ZERO,
          );
          return {
            amount: res.baseAmount,
            quotation: res.quotation,
          };
        } else if (baseAmount) {
          const res = await sdkContext?.perp?.observer.inquireByBase(
            pair.instrumentAddr,
            pair.expiry,
            tradeSide,
            baseAmount || ZERO,
          );
          return {
            amount: res.quoteAmount,
            quotation: res.quotation,
          };
        }

        const {
          trade: { chainMarketFormState: chainMarketFormState2 },
        } = getState() as AppState;
        const state2 = _.get(chainMarketFormState2, [chainId]);
        console.log('🚀 ~ file: actions.ts:829 ~ state2:', state2);
      }
    } catch (e) {
      const parsedError = parsedEthersError(e);
      if (parsedError) {
        dispatch(
          updateSimulateError({
            type: tradeType,
            errMessage: parsedError?.errorData || parsedError?.errorMsg,
            chainId,
          }),
        );
      }
    }
  },
);

export const batchPlaceScaledLimitOrder = createAsyncThunk(
  'trade/batchPlaceScaledLimitOrder',
  async (
    {
      signer,
      chainId,
      userAddr,
      sdkContext,
      pair,
      deadline,
      simulation,
      provider,
      side,
      leverageWad,
      baseSizeWad,
      sendTxNotification,
    }: {
      chainId: number;
      userAddr: string;
      signer: JsonRpcSigner;
      pair: WrappedPair;
      sdkContext: Context;
      deadline: number;
      simulation: IScaledOrderSimulationData;
      provider: JsonRpcProvider;
      side: Side;
      leverageWad: BigNumber;
      baseSizeWad: BigNumber;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch, getState },
  ): Promise<ContractReceipt | undefined> => {
    try {
      const adjustTradeSide = side; // getAdjustTradeSide(side, pair.isInverse);
      const ticks: number[] = simulation.orders.map((o) => o.tick);
      const ratios: number[] = simulation.orders.map((o) => o.ratio);
      const orderCount = simulation.orders.length;

      const sendingTemplate = parseSendingTxMessageMapping[OPERATION_TX_TYPE.BATCH_PLACE_SCALED_ORDER]!(
        pair.rootInstrument.baseToken.symbol,
        simulation.upperPrice,
        simulation.lowerPrice,
        simulation.baseSize,
        pair.rootInstrument.isInverse,
        side,
        orderCount,
      );

      const result = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record(
              'tx',
              `Try to send [${OPERATION_TX_TYPE.PLACE_ORDER}] operation tx for pair [${
                pair.rootInstrument.symbol
              }-${showProperDateString({ expiry: pair.expiry, format: 'MMDD' })}]`,
              undefined,
              {
                signer,
                expiry: pair.expiry,
                pair: pair,
                futures: pair.rootInstrument,
                base: formatEther(baseSizeWad),
                leverage: formatEther(leverageWad),
                deadline: addedDeadline(deadline),
                adjustTradeSide,
              },
            );
            // return await provider.getTransaction(`0x2f3c82087c8ec0b355e9f669e615ce6666b01ff1b1ced3ad792489e74a42085f`);
            const {
              global: { onChainReferralCode },
            } = getState() as AppState;
            const referralCode = onChainReferralCode?.referralCode || '';

            const params = {
              instrumentAddr: pair.instrumentAddr,
              expiry: pair.expiry,
              leverage: leverageWad,
              side: adjustTradeSide,
              baseSize: baseSizeWad,
              ticks: ticks,
              ratios,
              deadline: addedDeadline(deadline),
              referralCode,
              isInverse: pair.isInverse,
            };
            const instrumentAddr = pair.rootInstrument.instrumentAddr;
            const txType = OPERATION_TX_TYPE.BATCH_PLACE_SCALED_ORDER;

            return handleSendAgentOrNormalTx({
              signer,
              provider,
              userAddr,
              chainId,
              instrumentAddr,
              sendingTemplate,
              sendTxNotification,
              txType,
              sdk: sdkContext,
              handlePopulatedTx: async () => {
                const populatedTx = await sdkContext.perp.instrument.batchPlaceLimitOrder(params, { from: userAddr });
                return populatedTx;
              },
              handleGelatoTxTargetData: async () => {
                const instrumentContract = getSynContract({
                  chainId,
                  type: 'instrument',
                  signerOrProvider: signer,
                  address: instrumentAddr,
                });

                const sign = utils.signOfSide(params.side);

                const txArgs = utils.encodeBatchPlaceParam(
                  params.expiry,
                  params.baseSize.mul(sign),
                  params.leverage,
                  params.ticks,
                  params.ratios,
                  params.deadline,
                  params.referralCode ?? DEFAULT_REFERRAL_CODE,
                );

                const targetData = instrumentContract.interface.encodeFunctionData('batchPlaceFor', [userAddr, txArgs]);
                return targetData;
              },
            });
          },

          chainId,
          userAddr,
          txParams: {
            type: OPERATION_TX_TYPE.BATCH_PLACE_SCALED_ORDER,
            instrument: {
              baseSymbol: pair.rootInstrument.baseToken.symbol,
              quoteSymbol: pair.rootInstrument.quoteToken.symbol,
              isInverse: pair.rootInstrument.isInverse,
            },
            extra: {
              upperPrice: simulation.upperPrice,
              lowerPrice: simulation.lowerPrice,
              side,
              orderCount,
              baseSize: simulation.baseSize,
            },
            sendingTemplate: sendingTemplate,
          },
          wssProvider: provider,
        }),
      ).unwrap();

      if (result?.status) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, pair.rootInstrument.instrumentAddr, pair.expiry),
        });
        // need update balance
        if (simulation?.margin?.gt(0)) {
          if (simulation.marginToDeposit.gt(0)) {
            dispatch(
              checkTokenAllowance({
                userAddress: userAddr,
                chainId,
                marginToken: pair.rootInstrument.quoteToken,
                block: result.blockNumber,
                sdk: sdkContext,
              }),
            );
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
            });
          }
          if (!simulation?.margin.eq(simulation?.marginToDeposit)) {
            dispatch(
              fetchGateBalanceAction({
                chainId,
                userAddr,
                tokens: [pair.rootInstrument.quoteToken],
                block: result.blockNumber,
              }),
            );
          }
        }
      }

      return result;
    } catch (e) {
      SentryService.captureException(e, {
        name: 'trade/batchPlaceScaledLimitOrder',
        chainId,
        userAddr,
        pair: pair?.toJSON(),
        deadline,
        side,
        leverageWad: JSON.stringify(leverageWad),
        baseSizeWad: JSON.stringify(baseSizeWad),
      });
      console.error('🚀 ~ file: actions.ts:125 ~ e:', e);
      throw e;
    }
  },
);

export const simulateCrossMarketOrder = createAsyncThunk(
  'trade/simulateCrossMarketOrder',
  async ({
    sdkContext,
    position,
    base,
    limitPrice,
    tradeSide,
    leverage,
    chainId,
    slippage,
    onlyCallFuncMode,
  }: {
    chainId: number;
    limitPrice: string | undefined;
    position: WrappedPosition | undefined;
    sdkContext: Context;
    base: WrappedBigNumber;
    leverage?: WrappedBigNumber;
    tradeSide: Side;
    slippage: number;
    onlyCallFuncMode?: boolean;
  }): Promise<ICrossMarketOrderSimulation | undefined> => {
    try {
      if (
        limitPrice &&
        // !portfolio?.rootPair.wrapAttribute('fairPrice').eq(limitPrice) &&
        !base.eq(0) &&
        !(leverage && leverage.eq(0))
      ) {
        if (position) {
          const userAddr = position.traderAddr;
          const portfolio = position.rootPortfolio;
          const rootInstrument = portfolio.rootInstrument;

          const limitRes = getLimitAlignPriceWad({
            limitPrice,
            isInverse: rootInstrument.isInverse,
          });
          if (!limitRes) return;
          const { tick: tickNumber } = limitRes;
          const adjustTradeSide = tradeSide; // getAdjustTradeSide(tradeSide, rootInstrument.isInverse);

          if (!leverage) return;

          const isLong = rootInstrument.isInverse ? tradeSide === Side.SHORT : tradeSide === Side.LONG;
          const firstQuoteSwapToTick = isLong ? tickNumber + 1 : tickNumber - 1;
          const secondQuoteSwapToTick = isLong ? tickNumber + 2 : tickNumber - 2;
          const firstQuoteQuotation = await fetchInstrumentInquireByTick(
            chainId,
            rootInstrument.instrumentAddr,
            portfolio.rootPair.expiry,
            firstQuoteSwapToTick,
          );
          const secondQuoteQuotation = await fetchInstrumentInquireByTick(
            chainId,
            rootInstrument.instrumentAddr,
            portfolio.rootPair.expiry,
            secondQuoteSwapToTick,
          );

          const result = await sdkContext.perp.simulate.simulateCrossMarketOrder({
            instrument: rootInstrument,
            leverage: leverage.wadValue,
            tradeInfo: position,
            slippage: perToTenThousands(slippage),
            side: adjustTradeSide,
            size: {
              base: base.wadValue,
            },
            priceInfo: tickNumber,
            isInverse: portfolio.rootPair.isInverse,
            inquireResult:
              firstQuoteQuotation && secondQuoteQuotation
                ? {
                    firstQuote: firstQuoteQuotation,
                    secondQuote: secondQuoteQuotation,
                  }
                : undefined,
          });

          const gateBalanceList =
            queryClient.getQueryData<IGateValueResponse | null>(QUERY_KEYS.BALANCE.GATE(chainId, userAddr))
              ?.portfolios || [];
          const gateBalanceInfo = (gateBalanceList || [])?.find(
            (balance) => balance.address === position.rootInstrument.quoteToken.address,
          );

          const marketSimulationData: ISimulationData = {
            origin: result.tradeSimulation,
            ...result.tradeSimulation,
            simulationMainPosition: new WrappedPosition(
              {
                ...result.tradeSimulation.postPosition,
                id: position.rootPortfolio.id,
                instrumentId: position.rootInstrument.id,
                pairId: position.rootPair.id,
                accountId: position.rootPortfolio.rootAccount.id,
                portfolioId: position.rootPortfolio.id,
                blockInfo: position.rootPortfolio.blockInfo!,
              },
              position.rootPortfolio,
            ),

            tradePrice: WrappedBigNumber.from(result.tradeSimulation.tradePrice),
            limitTick: result.tradeSimulation.limitTick,
            limitPrice: WrappedBigNumber.from(TickMath.getWadAtTick(result.tradeSimulation.limitTick)),
            priceChangeRate: WrappedBigNumber.from(result.tradeSimulation.priceImpact),
            marginToDeposit: calcMarginToDeposit(result.tradeSimulation.margin, gateBalanceInfo?.balance || 0),
            additionalFee: WrappedBigNumber.from(result.tradeSimulation.stabilityFee),
            estimatedTradeValue: WrappedBigNumber.from(result.tradeSimulation.tradeValue),
            tradingFee: WrappedBigNumber.from(result.tradeSimulation.tradingFee),
            // margin may be input
            margin: WrappedBigNumber.from(result.tradeSimulation.marginChanged),
            realized: WrappedBigNumber.from(result.tradeSimulation.realized),
            minTradeValue: WrappedBigNumber.from(position.rootInstrument.minTradeValue),
            leverage: WrappedBigNumber.from(result.tradeSimulation.leverage),
            exceedMaxLeverage:
              result.tradeSimulation.exceedMaxLeverage &&
              WrappedBigNumber.from(result.tradeSimulation.leverage).lt(leverage || 0),
          };
          const estimatedTradeValue = WrappedBigNumber.from(TickMath.getWadAtTick(tickNumber)).mul(
            result.orderSimulation.size.base,
          );
          const orderSimulationData: IOrderSimulationData = {
            origin: result.orderSimulation,
            ...result.orderSimulation,
            tickNumber,
            // limitPrice: WrappedBigNumber.from(TickMath.getWadAtTick(tickNumber)),
            // limitPrice: WrappedBigNumber.from(
            //   await sdkContext.perp.calc.getWadAtTick(portfolio.instrumentAddr, tickNumber),
            // ),
            limitPrice: getAdjustTradePrice(
              WrappedBigNumber.from(TickMath.getWadAtTick(tickNumber)),
              !!rootInstrument?.isInverse,
            ),
            marginToDeposit: calcMarginToDeposit(result.orderSimulation.margin, gateBalanceInfo?.balance || 0),
            margin: WrappedBigNumber.from(result.orderSimulation.margin),
            minFeeRebate: WrappedBigNumber.from(result.orderSimulation.minFeeRebate),
            estimatedTradeValue: estimatedTradeValue,
            leverage: result.orderSimulation.leverage,
            minOrderValue: rootInstrument.minOrderValue,
            baseSize: result.orderSimulation.size.base,
          };

          const tradeSize = WrappedBigNumber.from(result.tradeSimulation.size.base);
          const orderSize = WrappedBigNumber.from(result.orderSimulation.size.base);

          const simulateResult: ICrossMarketOrderSimulation = {
            data: {
              ...result,
              tickNumber,
              tradeSize: tradeSize,
              orderSize: orderSize,
              totalMinSize: WrappedBigNumber.from(tradeSize).add(result.minOrderSize),
              tradeSimulation: marketSimulationData,
              orderSimulation: orderSimulationData,
              marginToDeposit: WrappedBigNumber.from(marketSimulationData.marginToDeposit).add(
                orderSimulationData.marginToDeposit,
              ),
              margin: WrappedBigNumber.from(marketSimulationData.margin).add(orderSimulationData.margin),
            },
          };

          return simulateResult;
        }
      }
      return undefined;
    } catch (e) {
      SentryService.captureException(
        e,
        {
          name: 'trade/simulateCrossMarketOrder',
          position: position?.toJSON(),
          base: base?.stringValue,
          limitPrice,
          tradeSide,
          leverage: leverage?.stringValue,
          chainId,
          slippage,
          onlyCallFuncMode,
        },
        'info',
      );
      console.log('🚀 simulateCrossMarketOrder error', e);
      if (e instanceof AxiosError) {
        return { message: e.response?.data?.msg || e.response?.data?.message || e.message };
      }
      const parsedError = parsedEthersError(e);
      const errorMsg = parsedError?.errorMsg || _.get(e, ['reason']) || _.get(e, ['message']);
      if (errorMsg.startsWith(ERROR_MSG_LIMIT_PRICE_FAR_FROM_MARK) || errorMsg.includes(INVALID_PRICE)) {
        return;
      } else if (errorMsg && errorMsg.includes(ERROR_MSG_SIZE_TO_TICK_IS_TRIVIAL)) {
        return { message: getMappedRevertReason(ERROR_MSG_SIZE_TO_TICK_IS_TRIVIAL) };
      }
      return { message: errorMsg, errorData: parsedError?.errorData };
    }
  },
);

export const placeCrossMarketOrder = createAsyncThunk(
  'tradeForm/placeCrossMarketOrder',
  async (
    {
      signer,
      chainId,
      userAddr,
      sdkContext,
      pair,
      deadline,
      simulation,
      provider,
      side,
      sendTxNotification,
    }: {
      chainId: number;
      userAddr: string;
      signer: JsonRpcSigner;
      pair: WrappedPair;
      sdkContext: Context;
      slippage: number;
      deadline: number;
      simulation: ICrossMarketOrderData;
      provider?: JsonRpcProvider;
      side: Side;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch, getState },
  ): Promise<ContractReceipt | undefined> => {
    try {
      // const adjustTradeSide = side; // getAdjustTradeSide(side, pair.isInverse);

      const sendingTemplate = parseSendingTxMessageMapping[OPERATION_TX_TYPE.PLACE_CROSS_MARKET_ORDER]!(
        pair.rootInstrument.baseToken.symbol,
        simulation.tradeSimulation.tradePrice,
        simulation.orderSize.add(simulation.tradeSize),
        pair.rootInstrument.isInverse,
        side,
      );
      const result = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record(
              'tx',
              `Try to send [${OPERATION_TX_TYPE.PLACE_CROSS_MARKET_ORDER}] operation tx for pair [${
                pair.rootInstrument.symbol
              }-${showProperDateString({ expiry: pair.expiry, format: 'MMDD' })}]`,
              undefined,
              {
                signer,
                expiry: pair.expiry,
                pair: pair,
                futures: pair.rootInstrument,
                simulation,
              },
            );
            const {
              global: { onChainReferralCode },
            } = getState() as AppState;
            const referralCode = onChainReferralCode?.referralCode || '';

            const params = {
              instrumentAddr: pair.rootInstrument.instrumentAddr,
              expiry: pair.expiry,
              orderSize: simulation.orderSize.wadValue,
              tradeSize: simulation.tradeSize.wadValue,
              side: getAdjustTradeSide(side, pair.isInverse),
              tradeLimitTick: simulation.tradeSimulation.limitTick,
              orderTick: simulation.orderSimulation.tickNumber,
              tradeMargin: simulation.tradeSimulation.margin.wadValue,
              // swapTradePrice: simulation.tradeSimulation.tradePrice.wadValue,
              orderMargin: simulation.orderSimulation.margin.wadValue,
              deadline: addedDeadline(deadline),
              referralCode,
              isInverse: pair.isInverse,
            };

            const instrumentAddr = pair.rootInstrument.instrumentAddr;
            const txType = OPERATION_TX_TYPE.PLACE_CROSS_MARKET_ORDER;

            return handleSendAgentOrNormalTx({
              signer,
              provider,
              userAddr,
              chainId,
              instrumentAddr,
              sendingTemplate,
              sendTxNotification,
              txType,
              sdk: sdkContext,
              needBatchData: false,
              handlePopulatedTx: async () => {
                const populatedTx = await sdkContext.perp.instrument.placeCrossMarketOrder(params, { from: userAddr });
                return populatedTx;
              },
              handleGelatoTxTargetData: async (agentAddr: string) => {
                const instrumentContract = getSynContract({
                  chainId,
                  type: 'instrument',
                  signerOrProvider: signer,
                  address: instrumentAddr,
                });

                const callDatas: string[] = [];
                const instrumentAddrs = [instrumentAddr, instrumentAddr];
                const sign = utils.signOfSide(params.side);

                const tradeData = instrumentContract.interface.encodeFunctionData('tradeFor', [
                  userAddr,
                  utils.encodeTradeParam({
                    expiry: params.expiry,
                    size: params.tradeSize.mul(sign),
                    amount: params.tradeMargin,
                    limitTick: params.tradeLimitTick,
                    deadline: params.deadline,
                    referralCode: params.referralCode ?? DEFAULT_REFERRAL_CODE,
                  }),
                ]);

                callDatas.push(tradeData);

                const placeData = instrumentContract.interface.encodeFunctionData('placeFor', [
                  userAddr,
                  utils.encodePlaceParam({
                    expiry: params.expiry,
                    size: params.orderSize.mul(sign),
                    amount: params.orderMargin,
                    tick: params.orderTick,
                    deadline: params.deadline,
                    referralCode: params.referralCode ?? DEFAULT_REFERRAL_CODE,
                  }),
                ]);
                callDatas.push(placeData);

                const routerContract = getSynContract({
                  chainId,
                  type: 'router',
                  signerOrProvider: signer,
                });
                console.log('🚀 ~ agentAddr:', { agentAddr, userAddr });

                const targetData = routerContract.interface.encodeFunctionData('batchMulticall', [
                  agentAddr, // signer address
                  userAddr,
                  instrumentAddrs, // targetContract address
                  callDatas,
                  false, // userPaid
                ]);
                console.log('🚀 ~ targetData:', targetData);

                return targetData;
              },
            });
          },

          chainId,
          userAddr,
          txParams: {
            type: OPERATION_TX_TYPE.PLACE_CROSS_MARKET_ORDER,
            instrument: {
              baseSymbol: pair.rootInstrument.baseToken.symbol,
              quoteSymbol: pair.rootInstrument.quoteToken.symbol,
              isInverse: pair.rootInstrument.isInverse,
            },
            sendingTemplate: sendingTemplate,
          },
          wssProvider: provider,
        }),
      ).unwrap();
      if (result?.status) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, pair.rootInstrument.instrumentAddr, pair.expiry),
        });
        // need update balance
        if (simulation?.margin?.gt(0)) {
          if (simulation.marginToDeposit.gt(0)) {
            dispatch(
              checkTokenAllowance({
                userAddress: userAddr,
                chainId,
                marginToken: pair.rootInstrument.quoteToken,
                block: result.blockNumber,
                sdk: sdkContext,
              }),
            );

            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
            });
          }
          if (!simulation?.margin.eq(simulation?.marginToDeposit)) {
            dispatch(
              fetchGateBalanceAction({
                chainId,
                userAddr,
                tokens: [pair.rootInstrument.quoteToken],
                block: result.blockNumber,
              }),
            );
          }
        }
      }
      return result;
    } catch (e) {
      SentryService.captureException(e, {
        name: 'tradeForm/placeCrossMarketOrder',
        chainId,
        userAddr,
        pair: pair?.toJSON(),
        deadline,
        side,
      });
      console.log('🚀 ~ file: actions.ts:125 ~ e:', e);
      throw e;
    }
  },
);

export const fillLimitOrder = createAsyncThunk(
  'trade/fillLimitOrder',
  async (
    {
      sdkContext,
      chainId,
      signer,
      order,
      userAddr,
      portfolio,
      deadline,
      provider,
      sendTxNotification,
    }: {
      chainId: number;
      sdkContext: Context;
      signer: JsonRpcSigner;
      userAddr: string;
      order: WrappedOrder;
      portfolio: WrappedPortfolio;
      deadline: number;
      provider: JsonRpcProvider;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch },
  ): Promise<ContractReceipt | undefined> => {
    try {
      const pair = portfolio.rootPair;
      const sendingTemplate = parseSendingTxMessageMapping[OPERATION_TX_TYPE.FILL_ORDER]!(order.tick, pair.isInverse);

      const result = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record(
              'tx',
              `Try to send [${OPERATION_TX_TYPE.FILL_ORDER}] operation tx for pair [${portfolio.rootInstrument.symbol}`,
              undefined,
              {
                signer,
                portfolio,
                order,
                deadline: addedDeadline(deadline),
              },
            );

            const params = {
              instrumentAddr: pair.rootInstrument.instrumentAddr,
              expiry: pair.expiry,
              nonce: order.nonce,
              tick: order.tick,
              target: userAddr,
              // isInverse: pair.isInverse,
            };
            const instrumentAddr = pair.rootInstrument.instrumentAddr;
            const txType = OPERATION_TX_TYPE.FILL_ORDER;

            return handleSendAgentOrNormalTx({
              signer,
              provider,
              userAddr,
              chainId,
              instrumentAddr,
              sendingTemplate,
              sendTxNotification,
              txType,
              sdk: sdkContext,
              handlePopulatedTx: async () => {
                const populatedTx = await sdkContext.perp.instrument.fillLimitOrder(params, { from: userAddr });
                return populatedTx;
              },
              handleGelatoTxTargetData: async () => {
                const instrumentContract = getSynContract({
                  chainId,
                  type: 'instrument',
                  signerOrProvider: signer,
                  address: instrumentAddr,
                });

                const txArgs = utils.encodeFillParam(params);

                const targetData = instrumentContract.interface.encodeFunctionData('fill', [txArgs]);
                return targetData;
              },
            });
          },

          chainId,
          userAddr,
          txParams: {
            type: OPERATION_TX_TYPE.FILL_ORDER,
            instrument: {
              baseSymbol: pair.rootInstrument.baseToken.symbol,
              quoteSymbol: pair.rootInstrument.quoteToken.symbol,
              isInverse: pair.rootInstrument.isInverse,
            },
            sendingTemplate,
          },
          wssProvider: provider,
        }),
      ).unwrap();
      if (result?.status) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, pair.rootInstrument.instrumentAddr, pair.expiry),
        });
        dispatch(
          fetchGateBalanceAction({
            chainId,
            userAddr,
            tokens: [pair.rootInstrument.quoteToken],
            block: result.blockNumber,
          }),
        );
        _.unset(portfolio.orderMap, [order.id]);
      }
      if (result?.transactionHash) {
        const { setPollingHistoryTx } = useGlobalStore.getState();
        setPollingHistoryTx({
          chainId,
          userAddress: userAddr,
          pollingHistoryId: PollingHistoryId.order,
          tx: result?.transactionHash,
        });
      }
      return result;
    } catch (e) {
      SentryService.captureException(e, {
        name: 'trade/fillLimitOrder',
        chainId,
        order: order.id,
        userAddr,
        deadline,
      });
      console.log('🚀 ~ file: actions.ts:125 ~ e:', e);
      throw e;
    }
  },
);
